import React, {forwardRef, useCallback, useMemo, useState} from 'react';
import DatePicker from 'react-datepicker';
import {createPortal} from 'react-dom';

import {useFloatingNodeId} from '@floating-ui/react';
import {useZIndexStore} from '@pb/kit-component-floating';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {TimeInputWithToggle} from './time-input-with-toggle';
import {CustomCalendarHeader} from './custom-calendar-header';
import {defaultTodayButton} from './default-today-button';
import {getEffectiveDateFormat} from './utils/getEffectiveDateFormat';

import styles from './calendar.component.module.styl';

/**
 * $FlowIgnoreMe WHY: DatePicker has no Flow type definitions
 **/
import type {CustomHeaderProps} from './typedef';
import type {DatePickerProps} from 'react-datepicker';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

/**
 * $FlowIgnoreMe WHY: DatePicker has no Flow type definitions
 **/
export type CalendarCoreProps = DatePickerProps;

const Container: React.FC<{children?: React.ReactNode, style?: React.CSSProperties}> = ({children, style}) => {
    return children ? createPortal(
        <div style={style}>{children}</div>,
        document.body,
    ) : null;
};

/**
 * $FlowIgnoreMe WHY: $FlowIgnoreMe WHY: DatePicker has no Flow type definitions
 **/
export const CalendarCore = forwardRef<DatePicker, CalendarCoreProps>(
    (props, ref) => {
        const {
            calendarClassName,
            showWeekPicker,
            inline = false,
            renderCustomHeader = null,
            todayButton,
            showTimeInput = false,
            dateFormat,
            onCalendarOpen,
            onCalendarClose,
            ...calendarProps
        } = props;

        const [includeTime, setIncludeTime] = useState(false);
        const [isOpen, setIsOpen] = useState<boolean>(false);

        const nodeId = useFloatingNodeId();
        const {zIndex} = useZIndexStore(nodeId, isOpen);

        const PopperContainer = useCallback<React.FC<{children?: React.ReactNode}>>(
            ({children}) => (
                <Container style={{'--datepicker-z-index': zIndex} as React.CSSProperties}>
                    {children}
                </Container>
            ), [zIndex]);

        // Convert dateFormat to string if it's an array
        const dateFormatString = useMemo(
            () => (Array.isArray(dateFormat) ? dateFormat[0] : dateFormat),
            [dateFormat],
        );

        // Determine which format to use
        const effectiveFormat = useMemo(
            () => getEffectiveDateFormat(showTimeInput && includeTime, dateFormatString),
            [showTimeInput, includeTime, dateFormatString],
        );

        // Extract time format string directly from effectiveFormat
        const timeFormat = useMemo(() => {
            if (!effectiveFormat) {
                return undefined;
            }

            // Extract time portion from format (everything after date tokens)
            const timeMatch = effectiveFormat.match(/([HhKkmsS:.]+(?:\s*[aA]{1,2})?)$/);

            return timeMatch ? timeMatch[1].trim() : undefined;
        }, [effectiveFormat]);

        const renderHeader = (params: CustomHeaderProps) => <CustomCalendarHeader {...params}/>;

        const calendarClassNames = classNames(
            calendarClassName,
            {
                'react-datepicker_popover': !inline,
                'show-week-picker': showWeekPicker,
            });

        return (
            <div className={classes['calendar-new']}>
                <DatePicker
                    popperClassName={classes['calendar-new-popper']}
                    popperContainer={PopperContainer}
                    {...calendarProps}
                    ref={ref}
                    inline={inline}
                    showWeekPicker={showWeekPicker}
                    renderCustomHeader={renderCustomHeader ?? renderHeader}
                    calendarClassName={calendarClassNames}
                    timeInputLabel={' '}
                    showTimeInput={showTimeInput}
                    onCalendarOpen={() => {
                        setIsOpen(true);
                        onCalendarOpen?.();
                    }}
                    onCalendarClose={() => {
                        setIsOpen(false);
                        onCalendarClose?.();
                    }}
                    customTimeInput={showTimeInput ? (
                        <TimeInputWithToggle
                            timeFormat={timeFormat}
                            includeTime={includeTime}
                            onToggleChange={() => setIncludeTime(!includeTime)}
                        />
                    ) : undefined}
                    todayButton={
                        todayButton ??
                        defaultTodayButton({
                            selected: calendarProps.selected,
                            maxDate: calendarProps.maxDate,
                            minDate: calendarProps.minDate,
                        })
                    }
                    dateFormat={effectiveFormat}
                />
            </div>
        );
    },
);

CalendarCore.displayName = 'CalendarCore';
